import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { Megaphone, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { type AudienceFilter, audienceKeys, listAudience, plural } from "@/entities/audience"
import { Button } from "@/shared/ui/button"
import { Card } from "@/shared/ui/card"
import { EmptyState } from "@/shared/ui/empty-state"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { LoadSwap } from "@/shared/ui/load-swap"
import { BulkGroupMenu } from "@/features/manage-groups"
import { useStartBroadcast } from "@/features/send-broadcast"
import { botPath, useBotWorkspace } from "@/widgets/bot-workspace"
import { MemberRow } from "./member-row"
import { fetchingDim } from "@/shared/lib/motion"
import { useShownKey } from "@/shared/lib/use-shown-key"
import { TabText } from "@/shared/ui/tab-label"
import { MembersSkeleton } from "./audience-list-section"

const PAGE_SIZE = 30

/**
 * Люди по готовому фильтру: метка, шаг воронки. Подпись показывает, кто в списке;
 * «Показать всех» (если передан onReset) снимает ограничение шага.
 */
export function FilteredPeople({
	botId,
	filter,
	title,
	emptyText = "Под этот фильтр пока никто не попал.",
	onReset,
	broadcastName,
}: {
	botId: string
	filter: AudienceFilter
	title: string
	emptyText?: string
	onReset?: () => void
	/** С названием появляется кнопка «Рассылка по ним»: черновик сразу с этим фильтром. */
	broadcastName?: string
}) {
	const navigate = useNavigate()
	const startBroadcast = useStartBroadcast(botId)
	const tags = useBotWorkspace().draft?.draft?.tags ?? []
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const filterKey = JSON.stringify(filter)
	// biome-ignore lint/correctness/useExhaustiveDependencies: отметки сбрасываются, когда сменился сам список
	useEffect(() => setPicked(new Set()), [filterKey])
	const toggle = (id: string) =>
		setPicked((current) => {
			const next = new Set(current)
			if (!next.delete(id)) next.add(id)
			return next
		})
	const pickedFilter: AudienceFilter = { sessionIds: [...picked] }
	const people = useInfiniteQuery({
		queryKey: audienceKeys.list(botId, filter),
		queryFn: ({ pageParam }) => listAudience(botId, filter, { skip: pageParam, take: PAGE_SIZE }),
		initialPageParam: 0,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
			return loaded < last.total ? loaded : undefined
		},
		placeholderData: keepPreviousData,
	})
	const pages = people.data?.pages ?? []
	const members = pages.flatMap((page) => page.items)
	const variables = pages[0]?.variables ?? []
	const total = pages[0]?.total ?? 0
	// Смена фильтра (прошли ⇄ стоят) идёт переходом, когда пришли новые люди.
	const shownKey = useShownKey(JSON.stringify(filter), people.isPlaceholderData)

	return (
		<section className="space-y-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h3 className="flex items-baseline text-sm font-semibold">
					<TabText text={title} />
					<span className="ml-2 font-normal text-muted-foreground tabular-nums">
						{total} {plural(total, ["человек", "человека", "человек"])}
					</span>
				</h3>
				<div className="flex flex-wrap items-center gap-2">
					{onReset ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="rounded-full"
							onClick={onReset}
						>
							<X /> Показать всех
						</Button>
					) : null}
					{picked.size > 0 ? (
						<>
							<BulkGroupMenu
								botId={botId}
								filter={pickedFilter}
								total={picked.size}
								tags={tags}
								picked
								onDone={() => setPicked(new Set())}
							/>
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="rounded-full"
								disabled={startBroadcast.isPending}
								onClick={() =>
									startBroadcast.mutate({
										name: broadcastName ? `${broadcastName}, отмеченные` : "Отмеченные",
										filter: pickedFilter,
									})
								}
							>
								<Megaphone /> Рассылка отмеченным
							</Button>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								className="rounded-full"
								onClick={() => setPicked(new Set())}
							>
								Снять отметку
							</Button>
						</>
					) : total > 0 ? (
						<BulkGroupMenu botId={botId} filter={filter} total={total} tags={tags} />
					) : null}
					{broadcastName && total > 0 && picked.size === 0 ? (
						<Button
							type="button"
							size="sm"
							className="rounded-full"
							disabled={startBroadcast.isPending}
							onClick={() => startBroadcast.mutate({ name: broadcastName, filter })}
						>
							<Megaphone /> Рассылка по ним
						</Button>
					) : null}
				</div>
			</div>
			<LoadSwap
				loading={people.isLoading}
				id={people.isError ? "error" : members.length ? `list-${shownKey}` : `empty-${shownKey}`}
				className={fetchingDim(people.isPlaceholderData)}
			>
				{people.isLoading ? (
					<MembersSkeleton withToggle={false} />
				) : people.isError ? (
					<LoadErrorState onRetry={() => void people.refetch()} />
				) : members.length === 0 ? (
					<EmptyState title="Пока никого">{emptyText}</EmptyState>
				) : (
					<Card className="overflow-hidden">
						<ul className="divide-y divide-border">
							{members.map((member) => (
								<MemberRow
									key={member.id}
									member={member}
									variables={variables}
									onOpen={() => navigate(botPath(botId, `analytics/users/${member.id}`))}
									selected={picked.has(member.id)}
									onToggle={() => toggle(member.id)}
								/>
							))}
						</ul>
					</Card>
				)}
			</LoadSwap>
			{people.hasNextPage ? (
				<Button
					type="button"
					variant="outline"
					className="w-full rounded-xl"
					disabled={people.isFetchingNextPage}
					onClick={() => void people.fetchNextPage()}
				>
					{people.isFetchingNextPage ? "Загружаем…" : "Показать ещё"}
				</Button>
			) : null}
		</section>
	)
}
